import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { PieChart, Frown, Meh, Smile } from 'lucide-react';

// Asignar íconos para cada logro con estilos mejorados
const iconoLogro = {
  "NO LO CONSIGUE": <Frown className="text-red-500 w-5 h-5 sm:w-7 sm:h-7 drop-shadow-md" />,
  "EN PROCESO": <Meh className="text-amber-500 w-5 h-5 sm:w-7 sm:h-7 drop-shadow-md" />, 
  "DOMINA EL LOGRO": <Smile className="text-emerald-500 w-5 h-5 sm:w-7 sm:h-7 drop-shadow-md" />,
};

const determinarLogroEquivalente = (porcentaje) => {
  if (porcentaje <= 66.00) return "NO LO CONSIGUE";
  if (porcentaje <= 86.00) return "EN PROCESO";
  if (porcentaje < 101.00) return "DOMINA EL LOGRO";
  return "DOMINA EL LOGRO";
};

// Colores más vibrantes y armoniosos
const COLORS = ['#6366F1', '#EC4899', '#10B981', '#F59E0B', '#8B5CF6'];

const EvaluationCharts = ({ reportes, evaluacionId }) => {
  if (!reportes || !evaluacionId) {
    return <div className="text-gray-500 text-xs sm:text-sm font-medium p-3 sm:p-4 text-center bg-gray-50 rounded-lg">No hay datos disponibles</div>;
  }

  const reporteActual = reportes[evaluacionId];
  if (!reporteActual) {
    return <div className="text-gray-500 text-xs sm:text-sm font-medium p-3 sm:p-4 text-center bg-gray-50 rounded-lg">No hay datos para esta evaluación</div>;
  }

  const { datosReporteProgreso, datosReporteResumen } = reporteActual;

  if (!datosReporteProgreso || !Array.isArray(datosReporteProgreso.data)) {
    return <div className="text-gray-500 text-xs sm:text-sm font-medium p-3 sm:p-4 text-center bg-gray-50 rounded-lg">No hay datos de progreso disponibles</div>;
  }

  const dataBarras = datosReporteProgreso.data.map((ambito, index) => ({
    name: ambito.ambito_aprendizaje,
    porcentaje: parseFloat(ambito.porcentaje_ambito),
    fill: COLORS[index % COLORS.length],
    logro: determinarLogroEquivalente(parseFloat(ambito.porcentaje_ambito)),
  }));

  const porcentajeTotal = parseFloat(datosReporteResumen.data.porcentaje_total);
  const nivelLogro = determinarLogroEquivalente(porcentajeTotal);

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="bg-white p-3 sm:p-6 rounded-xl shadow-lg border border-gray-100">
        <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2">
          <span className="w-1.5 sm:w-2 h-5 sm:h-6 bg-indigo-500 rounded-full"></span>
          Progreso por Ámbitos
        </h3>
        <div className="h-64 sm:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart layout="vertical" data={dataBarras} margin={{ left: 5, right: 5, top: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis 
                type="number"
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
                fontSize={10}
                tickMargin={4}
                stroke="#6B7280"
                tick={{ fontSize: '10px', fill: "#4B5563" }}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={120}
                tick={{ fontSize: '10px', fill: "#4B5563" }}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="bg-white shadow-xl rounded-lg p-2 sm:p-3 text-xs sm:text-sm border border-gray-100">
                        <p className="font-semibold text-gray-800">{payload[0].payload.name}</p>
                        <p className="text-gray-600 mt-1">Porcentaje: {payload[0].value.toFixed(1)}%</p>
                        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100">
                          {iconoLogro[payload[0].payload.logro]}
                          <span className="font-medium text-gray-700">{payload[0].payload.logro}</span>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar dataKey="porcentaje" radius={[0, 6, 6, 0]}>
                {dataBarras.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {datosReporteResumen && (
        <div className="bg-white p-3 sm:p-6 rounded-xl shadow-lg border border-gray-100">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2">
            <span className="w-1.5 sm:w-2 h-5 sm:h-6 bg-indigo-500 rounded-full"></span>
            Resumen General
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
            <div className="p-3 sm:p-4 bg-gradient-to-br from-indigo-50 to-white rounded-lg border border-indigo-100 shadow-sm flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 bg-indigo-500 rounded-full">
                <PieChart className="text-white w-4 h-4 sm:w-6 sm:h-6" />
              </div>
              <div>
                <p className="text-xs sm:text-sm text-indigo-600 font-medium">Porcentaje Total</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-800">{porcentajeTotal.toFixed(1)}%</p>
              </div>
            </div>
            <div className="p-3 sm:p-4 bg-gradient-to-br from-emerald-50 to-white rounded-lg border border-emerald-100 shadow-sm flex items-center gap-3 sm:gap-4">
              <div className="p-1.5 sm:p-2 bg-white rounded-full shadow-sm">
                {iconoLogro[nivelLogro]}
              </div>
              <div>
                <p className="text-xs sm:text-sm text-emerald-600 font-medium">Nivel de Logro</p>
                <p className="text-base sm:text-lg font-bold text-gray-800">{nivelLogro}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvaluationCharts;
